/**
 * Assistente de implantação de um cliente — Nexora Grid (frontend, US-2201).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 1.2.0
 *
 * v1.2 (US-4604, Sprint 46): cabeçalho com migalhas e selo da situação.
 *
 * Checklist dos seis passos (hub, Grafana, token dos agentes, liberação, primeiro administrador, conferência), cada um
 * com situação e mensagem; executar ou repetir (idempotente). O primeiro passo leva a empresa a "em implantação" e a
 * conferência final, a "ativa". Executa: integrador com a função de engenheiro de implantação ou gestor do sistema.
 * US-2203: prontidão do cliente no topo (sete itens, cada um com o link da tela que resolve) e o relatório em PDF.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, baixar, ErroApi } from "../api";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { podeAcessarRota as podeAbrirProntidao } from "../acessoRotas";
import { useSessao } from "../sessao";
import { CabecalhoTela, Selo } from "../componentes/Tela";
import { quando } from "./tipos";

type Passo = { passo: string; nome: string; situacao: "pendente" | "executando" | "concluido" | "erro"; mensagem: string;
  tentativas: number; atualizado_em: string | null; atualizado_por: string };
type Item = { item: string; titulo: string; estado: "ok" | "atencao" | "pendente" | "nao_se_aplica"; detalhe: string; tela: string };
type Prontidao = { pronta: boolean; itens: Item[] };
const ESTADO: Record<Item["estado"], [string, string]> = { ok: ["OK", "situacao-validado"], atencao: ["Atenção", "situacao-pendente"],
  pendente: ["Pendente", "situacao-rejeitado"], nao_se_aplica: ["Não se aplica", ""] };
type Situacao = { empresa: string; situacao: string; passos: Passo[]; pode_executar: boolean };

const SITUACAO: Record<Passo["situacao"], [string, string]> = { pendente: ["Pendente", ""], executando: ["Executando", "situacao-pendente"],
  concluido: ["Concluído", "situacao-validado"], erro: ["Erro", "situacao-rejeitado"] };

export function Implantacao() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const [dados, setDados] = useState<Situacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [rodando, setRodando] = useState<string | null>(null);
  const [adm, setAdm] = useState({ login: "", nome: "", email: "" });
  const [prontidao, setProntidao] = useState<Prontidao | null>(null);
  const carregar = useCallback(() => {
    api<Situacao>(`/v1/admin/implantacao/${id}`).then(setDados).catch((x) => setErro(x instanceof ErroApi ? x.message : "Falha de rede."));
    api<Prontidao>(`/v1/admin/implantacao/${id}/prontidao`).then(setProntidao).catch(() => setProntidao(null));
  }, [id]);
  useEffect(carregar, [carregar, usuario]);
  if (!usuario) return null;
  if (erro) return <section className="admin"><p className="erro" role="alert">{erro}</p></section>;
  if (!dados || dados.empresa !== id) return <p className="nx-suave">carregando…</p>;
  if (!pode.verEmpresa(usuario, dados.empresa)) return <p className="nx-suave">Empresa não encontrada.</p>;
  const podeExecutar = dados.pode_executar && pode.executarImplantacao(usuario);
  const executar = async (passo: string) => {
    setRodando(passo);
    try {
      const corpo = passo === "administrador" && adm.login ? adm : undefined;
      await api(`/v1/admin/implantacao/${id}/passos/${passo}`, corpo ? { corpo } : { corpo: null });
    } catch (x) { setErro(x instanceof ErroApi ? x.message : "Falha de rede."); }
    finally { setRodando(null); carregar(); }
  };
  return (
    <section className="admin tl-pagina">
      <CabecalhoTela voltar={`/admin/empresas/${id}`} migalhas={[{ texto: NOMES.administracao, para: "/admin/empresas" }, { texto: NOMES.empresas, para: "/admin/empresas" },
        { texto: String(id), para: `/admin/empresas/${id}` }, { texto: "Implantação" }]} titulo={`Implantação · ${id}`}
        selos={<Selo tom={dados.situacao === "ativa" ? "ok" : "atencao"}>{dados.situacao.replace("_", " ")}</Selo>} />
      <p className="nx-suave">Cada passo pode ser repetido; o que deu errado fica registrado. O primeiro passo coloca a empresa em implantação; a conferência final a deixa ativa.
        {!podeExecutar && " Executar exige a função de engenheiro de implantação ou de gestor do sistema."}</p>
      {prontidao && (
        <section className="filiais" aria-label="Prontidão do cliente">
          <div className="linha-titulo"><h3>Prontidão do cliente</h3>
            <span className={`nx-selo ${prontidao.pronta ? "situacao-validado" : "situacao-pendente"}`}>{prontidao.pronta ? "Pronta para entrega" : "Há itens pendentes"}</span>
            <span className="espaco" />
            <button type="button" className="nx-botao" onClick={() => void baixar(`/v1/admin/implantacao/${id}/prontidao.pdf`, `implantacao-${id}.pdf`)}>Baixar relatório (PDF)</button></div>
          <table className="tabela tabela-usuarios">
            <tbody>{prontidao.itens.map((i) => (
              <tr key={i.item}><td><span className={`nx-selo ${ESTADO[i.estado][1]}`}>{ESTADO[i.estado][0]}</span></td>
                <td><b>{i.titulo}</b><br /><small className="nx-suave">{i.detalhe}</small></td>
                <td>{i.estado !== "ok" && i.estado !== "nao_se_aplica" && podeAbrirProntidao(usuario, i.tela) && <Link to={i.tela}>resolver</Link>}</td></tr>
            ))}</tbody>
          </table>
        </section>
      )}
      <h3>Passos da implantação</h3>
      <ol className="passos-implantacao">
        {dados.passos.map((p) => {
          const [rotulo, classe] = SITUACAO[p.situacao];
          return (
            <li key={p.passo} className="nx-cartao">
              <div className="linha-titulo">
                <b>{p.nome}</b> <span className={`nx-selo ${classe}`}>{rotulo}</span><span className="espaco" />
                {podeExecutar && <button type="button" className="nx-botao" disabled={rodando !== null}
                  onClick={() => void executar(p.passo)}>{rodando === p.passo ? "Executando…" : p.situacao === "pendente" ? "Executar" : "Repetir"}</button>}
              </div>
              {p.mensagem && <p className={p.situacao === "erro" ? "erro" : "nx-suave"}>{p.mensagem}</p>}
              {p.atualizado_em && <small className="nx-suave">{quando(p.atualizado_em)} por {p.atualizado_por} · {p.tentativas} tentativa(s)</small>}
              {p.passo === "administrador" && podeExecutar && p.situacao !== "concluido" && (
                <div className="form-filial">
                  <div className="nx-campo"><label htmlFor="imp-login">Login do administrador</label>
                    <input id="imp-login" value={adm.login} onChange={(e) => setAdm({ ...adm, login: e.target.value.trim().toLowerCase() })} /></div>
                  <div className="nx-campo"><label htmlFor="imp-nome">Nome</label>
                    <input id="imp-nome" value={adm.nome} onChange={(e) => setAdm({ ...adm, nome: e.target.value })} /></div>
                  <div className="nx-campo"><label htmlFor="imp-email">E-mail (recebe o convite)</label>
                    <input id="imp-email" type="email" value={adm.email} onChange={(e) => setAdm({ ...adm, email: e.target.value.trim() })} /></div>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
